import React, { ReactText, FC } from 'react';
import { Placement } from 'popper.js';
import { gqlTalentUnionEnum as TALENT_UNION } from '@minecraft.graphql-types';
import _filter from 'lodash/filter';
import UncontrolledTooltip from '@minecraft.uncontrolled-tooltip';
import { useTranslation } from '@blocs.i18n';

export const NON_UNION = 'Non-Union';

export const TALENT_UNIONS_MAX_LENGTH = 24;

type TruncatedProps = {
  profileId: ReactText;
  talentUnionsNames: Array<string>;
  tooltipPlacement: Placement;
  className: string;
  'data-qa-id': string;
};

const TruncatedTalentUnions: FC<TruncatedProps> = ({
  talentUnionsNames,
  profileId,
  tooltipPlacement = 'top-start',
  className,
  'data-qa-id': dataQaId,
}) => {
  const talentUnions = [];
  const tooltipUnions = [];

  talentUnionsNames.forEach((name) => {
    if (talentUnions.join(', ').length + name.length > TALENT_UNIONS_MAX_LENGTH) {
      tooltipUnions.push(name);
    } else {
      talentUnions.push(name);
    }
  });
  const joinedTalentUnions = talentUnions.join(', ');

  if (tooltipUnions.length) {
    const tooltipId = `unionsTooltip${profileId}`;
    const joinedTooltipUnions = tooltipUnions.join(', ');

    return (
      <>
        <span className={className} data-qa-id={dataQaId} data-testid="talent-unions-container">
          {joinedTalentUnions} <span id={tooltipId}>+{tooltipUnions.length}</span>
        </span>
        <UncontrolledTooltip placement={tooltipPlacement} target={tooltipId}>
          {joinedTooltipUnions}
        </UncontrolledTooltip>
      </>
    );
  }

  return (
    <span className={className} data-qa-id={dataQaId} data-testid="talent-unions-container">
      {joinedTalentUnions}
    </span>
  );
};

type Props = {
  profileId: ReactText;
  unionsList: any;
  noTruncation?: boolean;
  tooltipPlacement?: Placement;
  className?: string;
  'data-qa-id'?: string;
};

export const FormattedTalentUnions: FC<Props> = ({
  profileId,
  unionsList,
  noTruncation = false,
  tooltipPlacement = 'top-start',
  className,
  'data-qa-id': dataQaId,
}) => {
  const { t } = useTranslation();

  if (Array.isArray(unionsList) && unionsList.length) {
    const talentUnions = _filter(unionsList, (union) => union.code !== TALENT_UNION.SAG_AFTRA);
    const talentUnionsNames = talentUnions.map((union) =>
      union.parentObject ? `${union.parentObject.name} ${union.name}` : union.name
    );

    if (noTruncation) {
      return (
        <span className={className} data-qa-id={dataQaId} data-testid="talent-unions-container">
          {talentUnionsNames.join(', ')}
        </span>
      );
    }

    return (
      <TruncatedTalentUnions
        talentUnionsNames={talentUnionsNames}
        profileId={profileId}
        tooltipPlacement={tooltipPlacement}
        className={className}
        data-qa-id={dataQaId}
      />
    );
  }

  return (
    <span className={className} data-qa-id={dataQaId}>
      {t('common:profile.nonUnion')}
    </span>
  );
};

export default FormattedTalentUnions;
